// functions.store.ts
import { defineStore } from "pinia";
import { createRequestFunction, createCallableFunction } from "@/firebase/functions";
import { Auth } from "@/firebase/auth";
import { HttpsCallableResult } from "@firebase/functions-types";
import { StoreFunctionPayload, FirebaseFunctionResponse, NotificationParams } from "@/types/store";
import { useApplicationStore } from "./application.store";
import { callOnRequestFunctionStream, StreamedFunctionResponse } from "./helpers/functions/onRequest";
import { performanceMonitor } from '@/router/core/performance-monitor';
import { performanceCache } from '@/utils/performance-cache';

// Debug flag to enable/disable detailed logging
const DEBUG = true;

// Helper function for debug logging
function debugLog(message: string, data?: any) {
  if (DEBUG) {
    console.log(`[FunctionsStore] ${message}`, data || '');
  }
}

const initialState = () => ({}); // Define initial state

export const useFunctionsStore = defineStore({
  id: "functions",

  state: initialState,

  actions: {
    // Build a sanitized preview of payloads for debug/perf metadata
    buildPayloadMetadata(payload: any): { sanitizedPayload: any; payloadPreview: string } {
      const SENSITIVE_KEYS = new Set(['token', 'sessionToken', 'idToken', 'authorization', 'auth', 'password', 'secret', 'apiKey', 'bearer']);
      const redact = (obj: any, depth: number = 0): any => {
        if (obj === null || obj === undefined) return obj;
        if (depth > 4) return '[Depth Limited]';
        if (Array.isArray(obj)) return obj.slice(0, 10).map(v => redact(v, depth + 1));
        if (typeof obj === 'object') {
          const out: Record<string, any> = {};
          const entries = Object.entries(obj).slice(0, 25);
          for (const [k, v] of entries) {
            out[k] = SENSITIVE_KEYS.has(k) ? '[REDACTED]' : redact(v, depth + 1);
          }
          return out;
        }
        if (typeof obj === 'string' && obj.length > 200) return obj.slice(0, 200) + '…';
        return obj;
      };
      const sanitizedPayload = redact(payload);
      let payloadPreview = '';
      try { payloadPreview = JSON.stringify(sanitizedPayload).slice(0, 300); } catch { payloadPreview = '[unserializable]'; }
      return { sanitizedPayload, payloadPreview };
    },
    async callFunction<T>(
      { function_name, function_payload }: StoreFunctionPayload<any>,
      notify = false,
      notificationParams?: NotificationParams,
      cacheOptions?: { bypassCache?: boolean; ttl?: number }
    ): Promise<FirebaseFunctionResponse<T>> {
      debugLog("Preparing to call", { function_name, function_payload });

      // Check demo mode Firebase operation blocking
      const { useDemo } = await import('@/composables/useDemo');
      const demo = useDemo();
      
      if (demo.isDemoMode.value && !demo.shouldAllowFirebaseOperation.value) {
        const error = demo.blockFirebaseOperation(`function call: ${function_name}`);
        debugLog(`❌ ${function_name} blocked in demo mode`, { error: error.message });
        
        if (notify) {
          useApplicationStore().showNotification(
            notificationParams || {
              title: `Firebase Operation Blocked`,
              message: error.message,
              theme: "warning",
              autoClose: true,
              isOpen: true,
              timeout: 5000,
            }
          );
        }

        return {
          error: true,
          errorInfo: error,
          data: null,
        } as any;
      }

      // Determine if this function call should be cached
      const isCacheable = this.shouldCacheFunction(function_name, function_payload);
      const { bypassCache = false } = cacheOptions || {};
      
      // For cacheable functions, use performance cache
      if (isCacheable && !bypassCache) {
        const cacheKey = this.buildCacheKey(function_name, function_payload);
        const cacheType = this.getCacheType(function_name, function_payload);
        const projectId = this.extractProjectId(function_payload);
        
        return await performanceCache.getOrSet(
          cacheKey,
          () => this.executeFunction(function_name, function_payload, notify, notificationParams),
          {
            type: cacheType,
            projectId,
            bypassCache,
            ttl: cacheOptions?.ttl
          }
        );
      }

      // For non-cacheable functions, execute directly
      return await this.executeFunction(function_name, function_payload, notify, notificationParams);
    },

    // Helper method to determine if a function should be cached
    shouldCacheFunction(function_name: string, payload: any): boolean {
      // Don't cache write operations
      const writeActions = ['add', 'update', 'delete', 'set', 'create', 'remove', 'patch'];
      const action = payload?.action?.toLowerCase() || '';
      
      if (writeActions.some(writeAction => action.includes(writeAction))) {
        return false;
      }
      
      // Don't cache functions that modify state
      const nonCacheableFunctions = [
        'usersTsOnCallSwitch',  // User project loading might change
        'paymentsOnCallStripeSwitch' // Payment operations
      ];
      
      if (nonCacheableFunctions.includes(function_name)) {
        return false;
      }
      
      // Cache read-like operations
      const cacheableFunctions = [
        'firekitTsOnCallFirestoreSwitch', // Firestore reads
        'algolia-getSecuredKey'            // Algolia API keys
      ];
      
      return cacheableFunctions.includes(function_name) && action === 'get' || action === 'list' || action === 'getTemporaryApiKey';
    },

    // Build cache key from function name and payload
    buildCacheKey(function_name: string, payload: any): string {
      // Safely get current user - Auth might not be initialized yet
      let userId = 'anonymous';
      try {
        const user = Auth.currentUser;
        userId = user?.uid || 'anonymous';
      } catch {
        // Auth not initialized yet, use anonymous
      }
      
      // Create a stable cache key based on the essential parameters
      const keyParts = [
        function_name,
        payload?.action || 'unknown',
        payload?.path || '',
        JSON.stringify(payload?.params || {}),
        JSON.stringify(payload?.payload || {}),
        userId
      ];
      
      return keyParts.join('|');
    },

    // Determine cache type for TTL selection
    getCacheType(function_name: string, payload: any): 'project_info' | 'user_data' | 'algolia_keys' | 'features' | 'groups' | 'default' {
      if (function_name === 'algolia-getSecuredKey') return 'algolia_keys';
      if (payload?.path?.includes('/features')) return 'features';
      if (payload?.path?.includes('/groups')) return 'groups';
      if (payload?.path?.includes('projects/')) return 'project_info';
      if (payload?.path?.includes('users/')) return 'user_data';
      return 'default';
    },

    // Extract project ID from payload for cache management
    extractProjectId(payload: any): string | undefined {
      const path = payload?.path || '';
      const projectMatch = path.match(/projects\/([^\/]+)/);
      return projectMatch ? projectMatch[1] : undefined;
    },

    // The actual function execution logic
    async executeFunction<T>(
      function_name: string,
      function_payload: any,
      notify = false,
      notificationParams?: NotificationParams
    ): Promise<FirebaseFunctionResponse<T>> {
      // 1) Log out currentUser + ID token snippet (handle Auth not being initialized)
      let user = null;
      let idToken: string | null = null;
      
      try {
        user = Auth.currentUser;
        if (user) {
          idToken = await user.getIdToken();
        }
      } catch (authError: any) {
        // Auth not initialized yet - this is OK for unauthenticated function calls
        // Some functions (like checkUserExistsByX) can be called without auth
        debugLog("Auth not yet initialized, proceeding without auth context", { 
          error: authError?.message,
          function_name 
        });
      }
      
      debugLog("Auth.currentUser & ID token snippet", {
        uid: user?.uid,
        tokenSnippet: idToken
          ? `${idToken.substring(0, 10)}…${idToken.slice(-10)}`
          : "no-token",
      });

      // 2) Build the callable (uses async initialization to ensure Functions is ready)
      const callable = await createCallableFunction<Record<string, any>, any>(function_name);

      try {
        // 3) Fire the call with performance timing
        const { sanitizedPayload, payloadPreview } = this.buildPayloadMetadata(function_payload);
        const timingId = performanceMonitor.startTiming(`function:${function_name}`, {
          type: 'firebase_callable',
          function: function_name,
          sanitizedPayload,
          payloadPreview
        });
        const result: HttpsCallableResult = await callable(function_payload);
        performanceMonitor.endTiming(timingId, true);
        debugLog(`✅ ${function_name} response`, result.data);

        // 4) Unwrap either v1 or v2 shape
        const raw = result.data;
        const resp = raw && raw.data !== undefined ? raw.data : raw;

        return resp as FirebaseFunctionResponse<T>;
      } catch (err: any) {
        performanceMonitor.endTiming(`function:${function_name}`, false, err?.message || String(err));
        debugLog(`❌ ${function_name} error`, {
          code: err.code,
          message: err.message,
          details: err.details,
        });

        if (notify) {
          useApplicationStore().showNotification(
            notificationParams || {
              title: `Error in ${function_name}`,
              message: err.message,
              theme: "danger",
              autoClose: true,
              isOpen: true,
              timeout: 5000,
            }
          );
        }

        return {
          error: true,
          errorInfo: err,
          data: null,
        } as any;
      }
    },

    async callOnRequestStream(
      functionName: string,
      payload: any,
      streamedResponse: StreamedFunctionResponse
    ): Promise<void> {
      const startTime = performance.now();
      debugLog(`Calling stream function: ${functionName}`, {
        payload,
        timestamp: new Date().toISOString()
      });

      try {
        const { sanitizedPayload, payloadPreview } = this.buildPayloadMetadata(payload);
        const timingId = performanceMonitor.startTiming(`function_stream:${functionName}`, {
          type: 'firebase_onRequest_stream',
          function: functionName,
          sanitizedPayload,
          payloadPreview
        });
        const result = await callOnRequestFunctionStream(functionName, payload, streamedResponse);
        performanceMonitor.endTiming(timingId, true);
        console.log('result', result);

        const executionTime = performance.now() - startTime;
        debugLog(`Stream function ${functionName} completed`, {
          executionTimeMs: executionTime.toFixed(2),
          timestamp: new Date().toISOString()
        });
      } catch (error: any) {
        performanceMonitor.endTiming(`function_stream:${functionName}`, false, error?.message || String(error));
        const executionTime = performance.now() - startTime;
        console.error(`[FunctionsStore] Error in stream function: ${functionName}`, {
          error,
          executionTimeMs: executionTime.toFixed(2),
          payload,
          timestamp: new Date().toISOString()
        });
        throw error; // Re-throw to allow caller to handle
      }
    },

    async callOnRequest(functionName: string, payload: any): Promise<any> {
      const startTime = performance.now();

      debugLog(`Calling onRequest function: ${functionName}`, {
        payload,
        timestamp: new Date().toISOString()
      });

      try {
        // Create and call the onRequest function with performance timing
        const { sanitizedPayload, payloadPreview } = this.buildPayloadMetadata(payload);
        const timingId = performanceMonitor.startTiming(`function_request:${functionName}`, {
          type: 'firebase_onRequest',
          function: functionName,
          sanitizedPayload,
          payloadPreview
        });
        const requestFunction = createRequestFunction(functionName);
        const result = await requestFunction(payload);
        performanceMonitor.endTiming(timingId, true);

        const executionTime = performance.now() - startTime;
        debugLog(`onRequest function ${functionName} completed successfully`, {
          executionTimeMs: executionTime.toFixed(2),
          timestamp: new Date().toISOString()
        });

        return result;
      } catch (error: any) {
        performanceMonitor.endTiming(`function_request:${functionName}`, false, error?.message || String(error));
        const executionTime = performance.now() - startTime;

        // Log detailed error information
        console.error(`[FunctionsStore] Error calling onRequest function: ${functionName}`, {
          function: functionName,
          message: error.message || 'Unknown error occurred',
          payload,
          executionTimeMs: executionTime.toFixed(2),
          timestamp: new Date().toISOString()
        });

        // Re-throw the error to allow caller to handle
        throw error;
      }
    },

    reset() {
      // Reset state to initial state
      Object.assign(this.$state, initialState());
    },
  },
});
